/**
 * Supabank Glossary - SSOT for Technical Term Definitions
 * 
 * Used for tooltips, help text, and educational content.
 * Each term has: definition (what it is) + analogy (familiar comparison)
 * 
 * @see dictionary.ts tooltips section for UI-specific copy
 * @see docs/brand-book/01-IDENTITY.md for educational approach guidelines
 */

export type GlossaryTerm = {
  term: string
  definition: string
  analogy: string
  icon?: string // Lucide icon name
  category?: 'core' | 'crypto' | 'banking' | 'compliance' | 'technical'
}

export const glossary: Record<string, GlossaryTerm> = {
  // === CORE PRODUCT TERMS ===
  liquidationAddress: {
    term: "Liquidation Address",
    definition: "A crypto wallet address that automatically converts incoming cryptocurrency to fiat currency and deposits it in your bank account.",
    analogy: "A smart mailbox that converts crypto letters into cash and deposits them directly in your bank.",
    icon: "wallet",
    category: "core",
  },
  payoutRoute: {
    term: "Payout Route",
    definition: "Your configured bank account destination where converted funds are automatically sent.",
    analogy: "Your money's home address—tell us once, and funds always know where to go.",
    icon: "route",
    category: "core",
  },
  virtualAccount: {
    term: "Virtual Account",
    definition: "A dedicated account number (like an IBAN) for receiving fiat payments that can auto-convert to crypto or settle in your main account.",
    analogy: "A P.O. Box for your business finances—separate address, same you.",
    icon: "credit-card",
    category: "core",
  },
  autoConvert: {
    term: "Auto-Convert",
    definition: "Automatic conversion of one currency to another without manual intervention.",
    analogy: "Autopilot for your money—set it once, conversions happen automatically.",
    icon: "refresh-cw",
    category: "core",
  },

  // === CRYPTO TERMS ===
  liquidity: {
    term: "Liquidity",
    definition: "The ability to convert assets (like crypto) into spendable cash (fiat) instantly without losing value.",
    analogy: "Like turning ice (assets) into water (cash) on demand—ready to flow anywhere.",
    icon: "droplet",
    category: "crypto",
  },
  stablecoin: {
    term: "Stablecoin",
    definition: "A cryptocurrency designed to maintain a stable value, usually pegged 1:1 to a fiat currency like USD (e.g., USDC, USDT).",
    analogy: "Digital dollars that live on the blockchain—same value, new technology.",
    icon: "circle-dollar-sign",
    category: "crypto",
  },
  blockchain: {
    term: "Blockchain",
    definition: "A decentralized, tamper-proof ledger that records all transactions across a network of computers.",
    analogy: "A Google Doc that everyone can see but no one can edit or delete—permanent and transparent.",
    icon: "blocks",
    category: "crypto",
  },
  wallet: {
    term: "Crypto Wallet",
    definition: "Software or hardware that stores your private keys and allows you to send/receive cryptocurrency.",
    analogy: "Like a bank app, but for crypto—you control the keys, you control the money.",
    icon: "wallet",
    category: "crypto",
  },
  onRamp: {
    term: "On-Ramp",
    definition: "A service that converts fiat currency (USD, EUR) into cryptocurrency.",
    analogy: "The highway entrance to the crypto world—dollars in, crypto out.",
    icon: "arrow-up-right",
    category: "crypto",
  },
  offRamp: {
    term: "Off-Ramp",
    definition: "A service that converts cryptocurrency back into fiat currency.",
    analogy: "The highway exit from crypto—crypto in, dollars in your bank.",
    icon: "arrow-down-left",
    category: "crypto",
  },

  // === BANKING TERMS ===
  rails: {
    term: "Payment Rails",
    definition: "The underlying infrastructure and networks that move money between banks (ACH, SEPA, SWIFT, SPEI, PIX).",
    analogy: "The digital highways and train tracks that money travels on—different routes for different destinations.",
    icon: "train-track",
    category: "banking",
  },
  settlement: {
    term: "Settlement",
    definition: "The final step when funds actually arrive and become available in your bank account—not just 'pending'.",
    analogy: "The moment cash hits your hand, not just a promise that it's coming.",
    icon: "check-circle",
    category: "banking",
  },
  ach: {
    term: "ACH",
    definition: "Automated Clearing House—the US electronic bank-to-bank transfer network. Typically settles in 1-3 business days.",
    analogy: "The standard mail of US banking—reliable, but not overnight.",
    icon: "landmark",
    category: "banking",
  },
  sepa: {
    term: "SEPA",
    definition: "Single Euro Payments Area—the EU electronic transfer network for euro payments. Usually settles within 1 day.",
    analogy: "The Schengen of money—free flow of euros across European borders.",
    icon: "euro",
    category: "banking",
  },
  swift: {
    term: "SWIFT",
    definition: "Society for Worldwide Interbank Financial Telecommunication—the global network for international wire transfers.",
    analogy: "The passport control of international money—thorough, but sometimes slow.",
    icon: "globe",
    category: "banking",
  },
  spei: {
    term: "SPEI",
    definition: "Sistema de Pagos Electrónicos Interbancarios—Mexico's real-time payment system.",
    analogy: "Mexico's express money highway—instant transfers between Mexican banks.",
    icon: "zap",
    category: "banking",
  },
  pix: {
    term: "PIX",
    definition: "Brazil's instant payment system that allows real-time transfers 24/7.",
    analogy: "Brazil's WhatsApp for money—instant, free, and everyone uses it.",
    icon: "smartphone",
    category: "banking",
  },
  iban: {
    term: "IBAN",
    definition: "International Bank Account Number—a standardized format for identifying bank accounts across borders.",
    analogy: "Your bank account's passport number—identifies it uniquely anywhere in the world.",
    icon: "id-card",
    category: "banking",
  },

  // === COMPLIANCE TERMS ===
  compliance: {
    term: "Compliance",
    definition: "Adherence to financial regulations (KYC/AML) designed to prevent fraud, money laundering, and ensure safety.",
    analogy: "The seatbelt of finance—it protects everyone, even if it takes a moment to buckle up.",
    icon: "shield-check",
    category: "compliance",
  },
  kyc: {
    term: "KYC",
    definition: "Know Your Customer—the process of verifying a user's identity before allowing financial transactions.",
    analogy: "The bouncer at the club—checking IDs to keep everyone safe.",
    icon: "user-check",
    category: "compliance",
  },
  aml: {
    term: "AML",
    definition: "Anti-Money Laundering—regulations and procedures designed to prevent criminals from disguising illegal funds as legitimate.",
    analogy: "Financial detective work—following the money to catch the bad guys.",
    icon: "search",
    category: "compliance",
  },

  // === TECHNICAL TERMS ===
  ledger: {
    term: "Ledger",
    definition: "An immutable record of all financial transactions—the source of truth for account balances.",
    analogy: "A digital book of truth that can never be erased or altered.",
    icon: "book",
    category: "technical",
  },
  api: {
    term: "API",
    definition: "Application Programming Interface—a way for software to communicate with other software.",
    analogy: "The waiter at a restaurant—takes your order (request) to the kitchen (server) and brings back your food (response).",
    icon: "plug",
    category: "technical",
  },
  webhook: {
    term: "Webhook",
    definition: "An automated message sent from one system to another when something happens.",
    analogy: "A text notification that tells your app 'hey, something just happened!'",
    icon: "bell",
    category: "technical",
  },
  sandbox: {
    term: "Sandbox",
    definition: "A testing environment where you can try things without real money or consequences.",
    analogy: "A video game practice mode—learn the controls before playing for real.",
    icon: "box",
    category: "technical",
  },
} as const

// Helper: Get all terms by category
export const getTermsByCategory = (category: GlossaryTerm['category']) =>
  Object.values(glossary).filter(term => term.category === category)

// Helper: Search terms by keyword
export const searchTerms = (query: string) =>
  Object.values(glossary).filter(term =>
    term.term.toLowerCase().includes(query.toLowerCase()) ||
    term.definition.toLowerCase().includes(query.toLowerCase())
  )

